import { config } from '@iheartjane/config';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  Button,
  Flex,
  Typography,
  useDesktopMediaQuery,
} from '@iheartjane/reefer';
import isEqual from 'lodash-es/isEqual';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useCartTopperRow } from '@jane/dm/internal';
import { useCartState } from '@jane/monolith/components/cart/cartFacade/useCartState';
import { ItemsCarousel } from '@jane/monolith/components/itemsCarousel';
import { FilterSortId } from '@jane/search/data-access';
import { ErrorBoundary } from '@jane/shared-ecomm/components';
import { useGetStore } from '@jane/shared/data-access';
/* eslint-disable-next-line no-restricted-imports */
import type { StoreSpecial } from '@jane/shared/models';
import { useEcommApp } from '@jane/shared/providers';
import { Zone } from '@jane/shared/types';

import { HitProductCard } from './hitProductCard';

import styles from './cartToppers.module.css';

const ITEM_MARGIN_AMOUNT = 12;

export interface Props {
  specials?: StoreSpecial[];
}
export const CartToppers = ({ specials }: Props) => {
  const { appMode } = useEcommApp();
  const { cart } = useCartState();
  const storeId = cart?.store?.id;
  const { data: store } = useGetStore(storeId || 0);
  const isDesktop = useDesktopMediaQuery({});
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [cartProductIds, setCartProductIds] = useState<number[]>([]);

  const showArrows = isDesktop || ['bloom', 'headless'].includes(appMode);
  const [rightArrowDisabled, setRightArrowDisabled] = useState(true);
  const onShowRightArrow = useCallback(
    (showRightArrow: boolean) => setRightArrowDisabled(!showRightArrow),
    []
  );
  /**
   * Javascript does shallow comparisons in these `useMemo` or `useCallback` functions
   * so in order to avoid unnecessary re-renders and re-fetches we need to compare the raw product ids
   * with a stateful variable to ensure that refetching is not done when the cart products
   * are the same
   *
   * Unfortunately, that means that `rawProductIds` will be re-calculated frequently
   */
  const rawProductIds = useMemo(
    () => cart?.products.map((product) => product.id) ?? [],
    [cart?.products]
  );

  /**
   * In order to continue to use the `cartProductIds` as a dependency in the `useCartTopperRow`
   * we need to ensure that it doesn't change too frequently and cause unnecessary re-fetches of the cart toppers
   *
   * What we are doing here is comparing the `cartProductIds` with the `rawProductIds` and only updating state if they are different
   * The outcome of this is that cart toppers will only be re-fetched if the cart products have changed
   */
  useEffect(() => {
    if (!isEqual(cartProductIds, rawProductIds)) {
      setCartProductIds(rawProductIds);
    }
  }, [cartProductIds, rawProductIds]);

  const { instance, isLoading } = useCartTopperRow({
    cartProductIds,
    searchAttributes: ['*'],
    storeId: Number(storeId),
  });

  useEffect(() => {
    instance?.onLoad();
  }, [instance]);

  if (!store || (!isLoading && !!instance && !instance?.products.length))
    return null;

  return (
    <ErrorBoundary withFallbackRender={false}>
      <Flex
        className={styles['container']}
        flexDirection="column"
        pb={20}
        px={24}
        mt={16}
      >
        <Flex
          alignItems="center"
          flexDirection="row"
          justifyContent="space-between"
          mt={40}
          mb={24}
        >
          <Typography branded variant="h2-semibold" as="h3">
            Add to your order
          </Typography>
          {showArrows && (
            <Flex gap={16}>
              <Button.Icon
                aria-label="Go back on add to your order carousel"
                variant="tertiary"
                icon={<ArrowLeftIcon />}
                disabled={carouselIndex === 0}
                onClick={() => setCarouselIndex(carouselIndex - 1)}
              />
              <Button.Icon
                aria-label="Go forward on add to your order carousel"
                variant="tertiary"
                icon={<ArrowRightIcon />}
                disabled={rightArrowDisabled}
                onClick={() => setCarouselIndex(carouselIndex + 1)}
              />
            </Flex>
          )}
        </Flex>
        <ItemsCarousel
          controlledIndex={{
            currentIndex: showArrows ? carouselIndex : null,
            onShowRightArrow,
          }}
          items={instance?.products ?? []}
          itemMargin={ITEM_MARGIN_AMOUNT}
          itemRenderer={({ item: cartTopper, index }) => (
            <HitProductCard
              algoliaIndexName={`menu-products-by-price-${config.algoliaEnv}`}
              appliedWeightFilter=""
              bucketName="cart-toppers"
              carouselView={true}
              columnPosition={index}
              productInstance={cartTopper}
              hit={cartTopper.attributes}
              key={`cart-topper-${cartTopper.objectId}`}
              listView={false}
              productLocation="Cart Toppers"
              searchState={{
                currentSort: {
                  id: FilterSortId.PriceAsc,
                  suffix: 'by-price-asc',
                },
              }}
              specials={specials}
              store={store}
              zone={Zone.Cart}
            />
          )}
        />
      </Flex>
    </ErrorBoundary>
  );
};
